"use client"

import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react'

export interface CookiePreferences {
  necessary: boolean
  analytics: boolean
  marketing: boolean
  functional: boolean
}

interface CookieContextType {
  preferences: CookiePreferences
  hasConsent: boolean
  updatePreferences: (preferences: CookiePreferences) => void
  resetConsent: () => void
  canUseAnalytics: () => boolean
  canUseMarketing: () => boolean
  canUseFunctional: () => boolean
}

const CookieContext = createContext<CookieContextType | undefined>(undefined)

interface CookieProviderProps {
  children: ReactNode
}

export function CookieProvider({ children }: CookieProviderProps) {
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
    functional: false,
  })
  const [hasConsent, setHasConsent] = useState(false)

  useEffect(() => {
    // Load preferences from localStorage
    const consent = localStorage.getItem("cookie-consent")
    const savedPreferences = localStorage.getItem("cookie-preferences")
    
    if (consent) {
      setHasConsent(true)
      
      if (savedPreferences) {
        try {
          const parsed = JSON.parse(savedPreferences)
          setPreferences(parsed)
        } catch (e) {
          console.error("Error parsing cookie preferences:", e)
          // Fallback to necessary only
          setPreferences({
            necessary: true,
            analytics: false,
            marketing: false,
            functional: false,
          })
        }
      }
    }
  }, [])

  const updatePreferences = (newPreferences: CookiePreferences) => {
    const timestamp = new Date().toISOString()
    localStorage.setItem("cookie-consent", "custom")
    localStorage.setItem("cookie-preferences", JSON.stringify(newPreferences))
    localStorage.setItem("cookie-consent-date", timestamp)
    
    setPreferences(newPreferences)
    setHasConsent(true)
    
    // Initialize analytics based on new preferences
    initializeAnalytics(newPreferences)
  }

  const initializeAnalytics = (prefs: CookiePreferences) => {
    if (typeof window === "undefined") return

    if (prefs.analytics && window.gtag) {
      // Initialize Google Analytics
      window.gtag('consent', 'update', {
        analytics_storage: 'granted',
        ad_storage: prefs.marketing ? 'granted' : 'denied',
        functionality_storage: prefs.functional ? 'granted' : 'denied'
      })
    } else {
      // Disable analytics
      if (window.gtag) {
        window.gtag('consent', 'update', {
          analytics_storage: 'denied',
          ad_storage: 'denied',
          functionality_storage: 'denied'
        })
      }
    }

    // Initialize Hotjar based on marketing consent
    if (prefs.marketing && window.hj) {
      window.hj('consent', true)
    }
  }

  const resetConsent = () => {
    localStorage.removeItem("cookie-consent")
    localStorage.removeItem("cookie-preferences")
    localStorage.removeItem("cookie-consent-date")
    
    setPreferences({
      necessary: true,
      analytics: false,
      marketing: false,
      functional: false,
    })
    setHasConsent(false)
    
    // Disable all analytics
    if (typeof window !== "undefined") {
      if (window.gtag) {
        window.gtag('consent', 'update', {
          analytics_storage: 'denied',
          ad_storage: 'denied',
          functionality_storage: 'denied'
        })
      }
    }
  }

  const canUseAnalytics = () => {
    return hasConsent && preferences.analytics
  }

  const canUseMarketing = () => {
    return hasConsent && preferences.marketing
  }

  const canUseFunctional = () => {
    return hasConsent && preferences.functional
  }

  const value: CookieContextType = {
    preferences,
    hasConsent,
    updatePreferences,
    resetConsent,
    canUseAnalytics,
    canUseMarketing,
    canUseFunctional,
  }

  return (
    <CookieContext.Provider value={value}>
      {children}
    </CookieContext.Provider>
  )
}

export function useCookieConsent() {
  const context = useContext(CookieContext)
  if (context === undefined) {
    throw new Error('useCookieConsent must be used within a CookieProvider')
  }
  return context
}

// Utility hooks for specific cookie types
export function useAnalyticsConsent() {
  const { canUseAnalytics } = useCookieConsent()
  return canUseAnalytics()
}

export function useMarketingConsent() {
  const { canUseMarketing } = useCookieConsent()
  return canUseMarketing()
}

export function useFunctionalConsent() {
  const { canUseFunctional } = useCookieConsent()
  return canUseFunctional()
}

// Higher-order component for conditional rendering based on consent
export function withCookieConsent<T extends object>(
  Component: React.ComponentType<T>,
  cookieType: 'analytics' | 'marketing' | 'functional' = 'analytics'
) {
  return function CookieConsentWrapper(props: T) {
    const { canUseAnalytics, canUseMarketing, canUseFunctional } = useCookieConsent()
    
    let hasConsent = false
    switch (cookieType) {
      case 'analytics':
        hasConsent = canUseAnalytics()
        break
      case 'marketing':
        hasConsent = canUseMarketing()
        break
      case 'functional':
        hasConsent = canUseFunctional()
        break
    }
    
    if (!hasConsent) {
      return null
    }
    
    return <Component {...props} />
  }
}

// Declare global types for analytics
declare global {
  interface Window {
    gtag: (...args: any[]) => void
    hj: (...args: any[]) => void
  }
}
